import { useRef, useState, type ChangeEvent } from 'react';
import { Avatar } from './bits';
import { Camera } from './icons';
import { uploadAvatar, AvatarUploadError } from '../../lib/uploadAvatar';
import { cn } from '../../lib/cn';

/**
 * An avatar preview with a tap-to-change camera badge. Picking an image uploads it
 * (compressed to a small square JPEG) to the player's own Storage folder and hands
 * the resulting URL back via {@link onUploaded}. Uses a plain file input so it works
 * everywhere — including the native iOS camera / photo-library sheet — with no extra
 * native plugin. While a photo is set and {@link onRemove} is provided, a small
 * "Remove photo" affordance reverts to the coloured monogram.
 */
export function AvatarUpload({
  uid,
  name,
  tone,
  src,
  size = 84,
  surface = 'light',
  onUploaded,
  onRemove,
  onBusyChange
}: {
  uid: string | null;
  name: string;
  tone: number;
  src?: string | null;
  size?: number;
  /** The surface the control sits on — sets the camera badge's ring colour. */
  surface?: 'light' | 'dark';
  onUploaded: (url: string) => void;
  onRemove?: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setWorking = (v: boolean) => {
    setBusy(v);
    onBusyChange?.(v);
  };

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Allow re-picking the same file later (the change event won't fire otherwise).
    e.target.value = '';
    if (!file) return;
    setError(null);
    setWorking(true);
    try {
      const url = await uploadAvatar(uid ?? '', file);
      onUploaded(url);
    } catch (err) {
      setError(err instanceof AvatarUploadError ? err.message : 'Couldn’t set your photo. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  const badgeRing = surface === 'dark' ? 'border-[#15151B]' : 'border-surface-top';

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <Avatar name={name || 'P'} tone={tone} size={size} src={src} className="ring-1 ring-black/5" />

        {busy && (
          <div
            className="absolute inset-0 grid place-items-center rounded-full bg-black/45"
            aria-live="polite"
          >
            <span className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-white/30 border-t-white" />
          </div>
        )}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy || !uid}
          aria-label={src ? 'Change profile photo' : 'Add a profile photo'}
          className={cn(
            'absolute -bottom-1 -right-1 grid h-9 w-9 place-items-center rounded-full border-[3px] bg-accent text-white shadow-soft transition-transform active:scale-90 disabled:opacity-60',
            badgeRing
          )}
        >
          <Camera width={16} height={16} strokeWidth={2.2} />
        </button>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
        />
      </div>

      {src && onRemove && !busy && (
        <button
          type="button"
          onClick={() => {
            setError(null);
            onRemove();
          }}
          className={cn(
            'tap mt-3 text-[12px] font-bold',
            surface === 'dark' ? 'text-white/70' : 'text-ink3'
          )}
        >
          Remove photo
        </button>
      )}

      {error && <p className="mt-2 max-w-[240px] text-center text-[12px] font-semibold text-danger">{error}</p>}
    </div>
  );
}
